import {call, put, select, take, takeEvery, takeLatest} from 'redux-saga/effects'
import every from 'lodash/every'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import mapValues from 'lodash/mapValues'
import pick from 'lodash/pick'
import {SubmissionError} from 'redux-form/lib/SubmissionError'

import * as addressValidationActions from '../../actions/addressValidation'
import * as addressValidationSelectors from '../../store/addressValidation'
import * as checkoutActions from '../../actions/checkout'
import * as formHelpers from '../../components/Forms/helpers'
import * as track from '../../track/track'
import * as userInteractionsActions from '../../actions/userInteractions'
import {
  formatShippingAddressPayloadForClient,
  formatShippingAddressPayloadForServer,
} from './helpers'
import {currentOrderId} from '../../store/events'
import {id as currentUserId} from '../../store/user'
import {getDefaultShippingAddress, saveShippingAddress} from '../../api/checkout'
import {formatAddressValues} from '../../reducers/checkout/helpers'
import {BAD_REQUEST, isBadResponse, isGoodResponse} from '../../api/statusCodes'
import {checkoutApiError, GET_SHIPPING, SAVE_SHIPPING} from '../../checkoutErrorCodes'
import {initialState as checkoutInitialState} from '../../reducers/checkout'

export function* getShippingAddressWorker() {
  try {
    yield put(checkoutActions.getShippingAddressPending())
    const orderId = yield select(currentOrderId)
    const userId = yield select(currentUserId)
    const [shippingAddressRes, checkedOutOrdersRes] = yield call(
      getDefaultShippingAddress,
      orderId,
      userId,
    )

    const shippingStatus = shippingAddressRes.status
    const checkedOutOrdersStatus = checkedOutOrdersRes.status
    const shippingResponsePayload = formatAddressValues('shipping', shippingAddressRes.json)
    const latestCheckedOutOrder = get(checkedOutOrdersRes, 'json.results[0]', {})
    const isMissingAllFields = every(
      pick(shippingResponsePayload, formHelpers.NON_NULLABLE_FULL_ADDRESS_FIELDS),
      (field) => isNil(field),
    )

    // checks to see if all fields are missing. if so, use fallback API request response, otherwise, save address
    // information provided from the current order's shipping address (even if incomplete)
    if (isGoodResponse(shippingStatus) && !isMissingAllFields) {
      yield put(checkoutActions.getShippingAddressSuccess(shippingResponsePayload))
    } else if (isGoodResponse(checkedOutOrdersStatus) && !isEmpty(latestCheckedOutOrder)) {
      const shippingAddress = pick(
        formatAddressValues('shipping', latestCheckedOutOrder),
        Object.keys(checkoutInitialState.shippingAddress),
      )
      yield put(checkoutActions.getShippingAddressSuccess(shippingAddress))
    } else if (isBadResponse(shippingStatus) || isBadResponse(checkedOutOrdersStatus)) {
      const status = isBadResponse(shippingStatus) ? shippingStatus : checkedOutOrdersStatus
      throw checkoutApiError(GET_SHIPPING, status)
    } else {
      // nothing went wrong with the api calls, we simply dont have shipping info stored
      // for this customer, so dont propagate any error
      yield put(checkoutActions.getShippingAddressFailure())
    }
  } catch (e) {
    yield put(checkoutActions.getShippingAddressFailure(e))
  }
}

export function* getShippingAddressSaga() {
  yield takeLatest([checkoutActions.ORDER_IS_VALID_PRECHECKOUT], getShippingAddressWorker)
}

/**
 * Interfaces with the address validation actions. Returns the correct payload
 * for saving to the server based on validation and user input.
 *
 * @param {Object} fields The address fields to validate
 * @returns {Object|undefined} The object to save or `undefined` to cancel submission
 */
export function* validateAddressWorker(fields) {
  yield put(addressValidationActions.validateAddress(fields))
  const {payload, type} = yield take([
    addressValidationActions.EDIT_INPUT_ADDRESS,
    addressValidationActions.UTILIZE_INPUT_ADDRESS,
    addressValidationActions.UTILIZE_VERIFIED_ADDRESS,
    addressValidationActions.VALIDATE_ADDRESS_FAILURE,
    userInteractionsActions.CLOSE_MODAL,
  ])
  if (
    type === addressValidationActions.EDIT_INPUT_ADDRESS ||
    type === addressValidationActions.VALIDATE_ADDRESS_FAILURE ||
    type === userInteractionsActions.CLOSE_MODAL
  ) {
    return undefined
  }
  if (type === addressValidationActions.UTILIZE_INPUT_ADDRESS) {
    return {...fields, ignoredVerifiedAddress: payload.bypassedVerifiedAddress}
  }
  const updates = yield select(addressValidationSelectors.recommendedUpdates)
  return {...fields, ...updates}
}

export function* handleServerValidationErrors(json, reject) {
  yield put(checkoutActions.saveShippingAddressFailure())
  const fields = yield call(formatShippingAddressPayloadForClient, json)
  yield call(
    reject,
    new SubmissionError({
      shipping: {
        address: mapValues(fields, ([error]) => error),
      },
    }),
  )
}

export function* saveShippingAddressWorker({payload: {fields, resolve, reject}}) {
  yield put(checkoutActions.saveShippingAddressPending())
  const addressToSave = yield call(validateAddressWorker, fields)
  if (!addressToSave) {
    yield put(checkoutActions.saveShippingAddressFailure())
    yield call(reject, new SubmissionError({}))
    return
  }

  const orderId = yield select(currentOrderId)
  let json
  let status
  try {
    ;({json, status} = yield call(
      saveShippingAddress,
      orderId,
      formatShippingAddressPayloadForServer(addressToSave),
    ))
    if (isBadResponse(status)) {
      throw new Error()
    }
    yield call(resolve)
    yield put(checkoutActions.saveShippingAddressSuccess(formatAddressValues('shipping', json)))
  } catch {
    if (status === BAD_REQUEST) {
      yield call(handleServerValidationErrors, json, reject)
      return
    }
    yield put(checkoutActions.saveShippingAddressFailure(checkoutApiError(SAVE_SHIPPING, status)))
    yield call(reject, new SubmissionError({}))
  }
}

export function* saveShippingAddressSaga() {
  yield takeLatest([checkoutActions.SAVE_SHIPPING_ADDRESS], saveShippingAddressWorker)
}

export function* trackShippingFormSubmittedWorker({error}) {
  yield call(track.checkoutSubmittedShippingInfo, !!error)
}

export function* trackShippingFormSubmittedSaga() {
  yield takeEvery(
    [checkoutActions.SAVE_SHIPPING_ADDRESS_FAILURE, checkoutActions.SAVE_SHIPPING_ADDRESS_SUCCESS],
    trackShippingFormSubmittedWorker,
  )
}
